import type { QueryClient } from '@tanstack/react-query';

import type { LiveActionContent } from '@/components/proposals/shared/useAutoSaveCoordination';
import { logger } from '@/lib/logger';
import { PROPOSAL_DECISION_TIMING, trackTiming } from '@/lib/timing';

import { getUserFriendlyMessage, isProposalAPIError } from '../core/errors';
import { createProposalClient } from '../core/ProposalClient';
import type { ProposalDetail, ProposalResolution } from '../core/types';
import {
  classifyAndReportExecutionOutcome,
  trackApproval,
} from '../realtime/approvalExecutionTracker';
import { buildApproveArgs } from '../utils/buildApproveArgs';
import { classifyExecutionOutcome } from '../utils/classifyExecutionOutcome';
import { showExecutionFailureToast } from '../utils/showExecutionFailureToast';
import { showStaleActionsNotice } from '../utils/showStaleActionsNotice';
import { proposalQueryKeys } from './keys';

/**
 * Emits the proposal_decision_timing beacon for an approve outcome — but only
 * on a genuine advance (executing handoff, or a sync resolution that didn't
 * fail). `decisionStartTime` is a `performance.now()` timestamp captured by
 * the caller (StickyFooter's dispatchApprove) right before dispatch, so this
 * measures click-to-settled latency even though the request runs detached.
 */
function trackApproveDecisionTiming(
  decisionStartTime: number | undefined,
  result: ProposalDetail,
  extra?: Record<string, unknown>
): void {
  if (decisionStartTime === undefined) {
    return;
  }
  if (result.resolution === 'executing') {
    trackTiming(PROPOSAL_DECISION_TIMING, decisionStartTime, {
      decision: 'approve',
      resolution_path: 'executing',
      ...extra,
    });
  } else if (
    result.resolution === 'approved' &&
    classifyExecutionOutcome(result).status === 'success'
  ) {
    // Only a genuinely APPROVED + successfully-executed sync resolution counts
    // as a successful synchronous approve. This runs on the reconcile path too
    // (freshData.resolution !== 'pending'), where another actor may have
    // rejected/withdrawn/failed the proposal — those carry no execution error,
    // so classifyExecutionOutcome reports 'success', and without the resolution
    // gate we would misattribute their terminal state as our synchronous
    // approve. Mirrors the `resolution === 'approved'` gate the polling reload
    // path uses.
    trackTiming(PROPOSAL_DECISION_TIMING, decisionStartTime, {
      decision: 'approve',
      resolution_path: 'sync',
      ...extra,
    });
  }
}

// Lifecycle only ever advances pending -> executing -> terminal, never back.
// Used to guard against an in-flight approve response clobbering a more
// advanced state already written by a WS `proposal.updated` event.
const RESOLUTION_RANK: Record<ProposalResolution, number> = {
  pending: 0,
  executing: 1,
  approved: 2,
  rejected: 2,
  failed: 2,
  withdrawn: 2,
};

/**
 * Runs the approve request detached from the triggering component: fetches
 * fresh action IDs, flips the cache optimistically, approves, then hands off
 * to the execution tracker (for `executing`) or classifies the outcome
 * inline (for a terminal resolution). Never throws — StickyFooter can
 * unmount mid-flight and this keeps running to completion.
 */
export async function submitApprovalInBackground(params: {
  queryClient: QueryClient;
  orgId: number;
  proposalId: number;
  localSelectedIds: number[] | undefined;
  liveContent: LiveActionContent[];
  suppressSuccessToast: boolean;
  /**
   * Invoked on every terminal failure path (fresh-fetch failure, decryption
   * error, or approve-request failure after the self-heal exhausts). The
   * proposal is rolled back to `pending` on these paths, so the caller uses
   * this to lift any autosave freeze it armed before dispatching — otherwise a
   * reused sidebar provider stays frozen and later edits never persist.
   */
  onFailure?: () => void;
  /**
   * Invoked specifically when the approve fails with STALE_ACTIONS — the
   * draft changed server-side while the user was reviewing. The proposal has
   * already been rolled back to `pending` and `onFailure` called before this
   * fires, so callers use it for surface-level recovery (e.g. resyncing the
   * editor to the latest server content).
   */
  onStaleActions?: () => void;
  /**
   * `performance.now()` timestamp captured by the caller right before
   * dispatch, used to emit the proposal_decision_timing beacon once this
   * detached request settles. Omitted (or left undefined) skips the beacon —
   * callers that don't care about timing don't need to pass it.
   */
  decisionStartTime?: number;
}): Promise<void> {
  const {
    queryClient,
    orgId,
    proposalId,
    localSelectedIds,
    liveContent,
    suppressSuccessToast,
    onFailure,
    onStaleActions,
    decisionStartTime,
  } = params;

  const client = createProposalClient(orgId);
  const detailKey = proposalQueryKeys.detail(orgId, proposalId);

  const previous = queryClient.getQueryData<ProposalDetail>(detailKey);
  // Captures the object actually stored by the optimistic write, so a
  // failure rollback can compare-and-set against it instead of unconditionally
  // overwriting whatever is in the cache — see restorePreviousIfUnchanged.
  // Read back via getQueryData rather than keeping the literal we passed in:
  // React Query's structural sharing may return a differently-referenced (but
  // deep-equal) object from setQueryData, so the literal itself is not a
  // reliable identity to compare against later.
  let optimistic: ProposalDetail | undefined;
  if (previous) {
    queryClient.setQueryData<ProposalDetail>(detailKey, {
      ...previous,
      resolution: 'approved',
    });
    optimistic = queryClient.getQueryData<ProposalDetail>(detailKey);
  }

  // Restores the pre-approval snapshot on a failure path — but only if the
  // cache still holds the optimistic value this attempt wrote. If a WS event
  // resolved this proposal while the fresh fetch/approve request was in
  // flight, the cache already reflects newer authoritative state (a different
  // object reference) and must not be clobbered with the stale `pending`
  // snapshot.
  const restorePreviousIfUnchanged = (): void => {
    if (!previous) {
      return;
    }
    const current = queryClient.getQueryData<ProposalDetail>(detailKey);
    if (current === optimistic) {
      queryClient.setQueryData<ProposalDetail>(detailKey, previous);
    }
  };

  // The approve response reflects server state at request time — if a WS
  // `proposal.updated` event already advanced the cache further (e.g. to a
  // terminal state) while the request was in flight, writing the response
  // would revert that newer state. Only write when the response is at least
  // as advanced as what's currently cached.
  const applyApproveResult = (next: ProposalDetail): void => {
    const current = queryClient.getQueryData<ProposalDetail>(detailKey);
    if (current) {
      const currentRank = RESOLUTION_RANK[current.resolution];
      const nextRank = RESOLUTION_RANK[next.resolution];
      // A strictly more-advanced cache value always wins — never downgrade
      // (e.g. our optimistic `approved` must not be reverted to a fresh
      // `executing`).
      if (currentRank > nextRank) {
        return;
      }
      // At EQUAL rank, both are terminal (rank 2) or the same stage. The cache
      // may already hold a NEWER authoritative WS result of the same rank that
      // carries execution data this response lacks. Only overwrite when the
      // cache still holds exactly this attempt's optimistic value — i.e. no WS
      // event has replaced it (a WS write swaps in a different object
      // reference). Otherwise a stale approve response would clobber the newer
      // WS state, losing its execution_result.
      if (currentRank === nextRank && current !== optimistic) {
        return;
      }
    }
    queryClient.setQueryData<ProposalDetail>(detailKey, next);
  };

  let freshData: ProposalDetail;
  try {
    freshData = await client.get(proposalId);
  } catch (err) {
    restorePreviousIfUnchanged();
    logger.error(
      '[approve] fresh fetch failed',
      err instanceof Error ? err : new Error(String(err)),
      { component: 'proposals', proposalId }
    );
    onFailure?.();
    showExecutionFailureToast({
      orgId,
      proposalId,
      message: getUserFriendlyMessage(err),
    });
    return;
  }

  // Another actor (or another tab) may have already advanced this proposal past
  // `pending` while we were dispatching. Attempting to approve it now would be
  // rejected by the backend, and the catch-path rollback would then clobber the
  // cache with the stale `pending` snapshot — surfacing the wrong status to the
  // user. Reconcile the cache to the authoritative fresh state and hand off
  // instead of attempting a doomed approve. `applyApproveResult` keeps the rank
  // guard, so a fresh `executing` never downgrades our optimistic `approved`.
  if (freshData.resolution !== 'pending') {
    applyApproveResult(freshData);
    void queryClient.invalidateQueries({
      queryKey: proposalQueryKeys.lists(orgId),
    });
    trackApproveDecisionTiming(decisionStartTime, freshData);
    if (freshData.resolution === 'executing') {
      trackApproval(orgId, proposalId, queryClient, { suppressSuccessToast });
    } else {
      classifyAndReportExecutionOutcome(freshData, orgId, {
        suppressSuccessToast,
      });
    }
    return;
  }

  const { effectiveSelectedIds, actionPayloads, decryptErrorCount } =
    buildApproveArgs(freshData, localSelectedIds, liveContent);

  if (liveContent.length > 0) {
    logger.debug('[approve] live content collected', {
      component: 'proposals',
      proposalId,
      liveContentCount: liveContent.length,
    });
  }

  if (decryptErrorCount > 0) {
    restorePreviousIfUnchanged();
    onFailure?.();
    showExecutionFailureToast({
      orgId,
      proposalId,
      proposalTitle: freshData.title,
      message: `Cannot approve: ${decryptErrorCount} action${decryptErrorCount > 1 ? 's' : ''} failed to decrypt.`,
    });
    return;
  }

  try {
    const result = await client.approve(
      proposalId,
      effectiveSelectedIds,
      actionPayloads
    );
    applyApproveResult(result);
    // The proposal has left the pending set. The advance's list refresh + the
    // WS `proposal.updated` event normally cover this, but neither is
    // guaranteed on this path (the caller may not advance, and the WS can be
    // disconnected — the tracker then resolves via polling), so invalidate the
    // list queries directly to keep pending list views from going stale.
    void queryClient.invalidateQueries({
      queryKey: proposalQueryKeys.lists(orgId),
    });
    // A `create_pipeline` proposal creates the org's deal pipeline via a
    // REST-backed schema endpoint that the CRM WebSocket store does NOT
    // refresh. Without this invalidation the Deals page keeps its stale
    // (empty) `['pipelines']` cache — so after approving the pipeline
    // zero-state's proposal, the zero-state persists instead of revealing the
    // new board. Mirror the invalidation that useApproveProposal (mutations.ts)
    // already does so both paths behave the same.
    if (
      result.actions?.some(
        a => a.proposed_action_type_name === 'create_pipeline'
      )
    ) {
      void queryClient.invalidateQueries({ queryKey: ['pipelines', orgId] });
    }
    trackApproveDecisionTiming(decisionStartTime, result);
    if (result.resolution === 'executing') {
      trackApproval(orgId, proposalId, queryClient, { suppressSuccessToast });
    } else {
      classifyAndReportExecutionOutcome(result, orgId, {
        suppressSuccessToast,
      });
    }
  } catch (err) {
    // The error to surface to the user. When the NO_ACTIONS_SELECTED self-heal
    // retry runs and then fails, its error is the terminal one and supersedes
    // the original — reporting the stale NO_ACTIONS_SELECTED would misdirect
    // remediation (the retry can hit stale-payload validation, a 409, etc.).
    let terminalError = err;
    if (
      isProposalAPIError(err) &&
      err.code === 'NO_ACTIONS_SELECTED' &&
      freshData.actions.length === 1
    ) {
      try {
        const freshActionIds = freshData.actions.map(a => a.id);
        await client.updateSelection(proposalId, freshActionIds);
        // Retry with the fresh action IDs and payloads — same stale-data
        // protection and live-content carry-over as the initial request.
        // Passing `undefined, undefined` here would drop both.
        const retry = await client.approve(
          proposalId,
          freshActionIds,
          actionPayloads
        );
        applyApproveResult(retry);
        // Same rationale as the initial-approve path: the proposal has left the
        // pending set, so refresh list views directly.
        void queryClient.invalidateQueries({
          queryKey: proposalQueryKeys.lists(orgId),
        });
        if (
          retry.actions?.some(
            a => a.proposed_action_type_name === 'create_pipeline'
          )
        ) {
          void queryClient.invalidateQueries({
            queryKey: ['pipelines', orgId],
          });
        }
        trackApproveDecisionTiming(decisionStartTime, retry, {
          retried: true,
        });
        if (retry.resolution === 'executing') {
          trackApproval(orgId, proposalId, queryClient, {
            suppressSuccessToast,
          });
        } else {
          classifyAndReportExecutionOutcome(retry, orgId, {
            suppressSuccessToast,
          });
        }
        return;
      } catch (retryErr) {
        logger.warn('[approve] NO_ACTIONS_SELECTED self-heal failed', {
          component: 'proposals',
          proposalId,
          retryErr,
        });
        terminalError = retryErr;
      }
    }

    // STALE_ACTIONS: the draft changed server-side while the user was
    // reviewing. Roll back, invalidate, reopen the gate, and surface a
    // neutral informational notice — this is NOT an error, so we must
    // NOT fall through to the generic error toast.
    if (
      isProposalAPIError(terminalError) &&
      terminalError.code === 'STALE_ACTIONS'
    ) {
      restorePreviousIfUnchanged();
      void queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.detail(orgId, proposalId),
      });
      onFailure?.();
      onStaleActions?.();
      logger.warn('[approve] stale actions — recovered, re-review required', {
        component: 'proposals',
        proposalId,
      });
      showStaleActionsNotice();
      return;
    }

    restorePreviousIfUnchanged();
    onFailure?.();
    logger.error(
      '[approve] request failed',
      terminalError instanceof Error
        ? terminalError
        : new Error(String(terminalError)),
      { component: 'proposals', proposalId }
    );
    showExecutionFailureToast({
      orgId,
      proposalId,
      proposalTitle: freshData.title,
      message: getUserFriendlyMessage(terminalError),
    });
  }
}
